import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { formatPhone, type LiveCall, type LiveCallStatus } from '@/lib/calls/liveCalls';

/**
 * Presentation vocabulary shared by every piece of the Live calls page, so a
 * status reads the same in the tabs, the list, the drawer and the history.
 */

export type LiveStage = 'waiting' | 'talking' | 'holding' | 'automated' | 'ended';

export const STAGE_ORDER: LiveStage[] = ['waiting', 'talking', 'holding', 'automated', 'ended'];

export const STAGE_TITLES: Record<LiveStage, string> = {
  waiting: 'Waiting',
  talking: 'Talking',
  holding: 'On hold',
  automated: 'AI & menus',
  ended: 'Just ended',
};

/**
 * Who needs looking at first: a caller still waiting, then one parked on hold,
 * then a conversation in progress. Shared so the page, the global bubble and
 * the monitoring sheet all put the same call at the top.
 */
export const STAGE_RANK: Record<LiveStage, number> = {
  waiting: 0,
  holding: 1,
  talking: 2,
  automated: 3,
  ended: 4,
};

/** Most urgent first, then longest-running. */
export function byUrgency(a: LiveCall, b: LiveCall): number {
  return STAGE_RANK[stageOf(a)] - STAGE_RANK[stageOf(b)] || a.startedAt - b.startedAt;
}

export function stageOf(call: LiveCall, ended = false): LiveStage {
  if (ended || call.status === 'ending') return 'ended';
  switch (call.status) {
    case 'ringing':
    case 'dialing':
      return 'waiting';
    case 'connected':
      return 'talking';
    case 'on_hold':
    case 'transferring':
      return 'holding';
    default:
      return 'automated';
  }
}

interface StatusMeta {
  label: string;
  /** Dot colour. */
  dot: string;
  /** Whether the dot pulses — something is actively happening. */
  pulse: boolean;
}

export const STATUS_META: Record<LiveCallStatus | 'ended', StatusMeta> = {
  ringing: { label: 'Ringing', dot: 'bg-warning-border', pulse: true },
  dialing: { label: 'Dialing', dot: 'bg-warning-border', pulse: true },
  // The live accent, not green: the same colour the global pill and panel use
  // for "this line is open", and legible on their dark surface, where the
  // muted sage of `success-border` all but vanished.
  connected: { label: 'Connected', dot: 'bg-portal-accent', pulse: false },
  on_hold: { label: 'On hold', dot: 'bg-info-border', pulse: false },
  transferring: { label: 'Transferring', dot: 'bg-info-border', pulse: true },
  ai: { label: 'With AI', dot: 'bg-brand-purple/50', pulse: false },
  ivr: { label: 'In phone menu', dot: 'bg-muted-foreground/40', pulse: false },
  voicemail: { label: 'Voicemail', dot: 'bg-muted-foreground/40', pulse: false },
  ending: { label: 'Ending', dot: 'bg-muted-foreground/30', pulse: false },
  ended: { label: 'Ended', dot: 'bg-muted-foreground/30', pulse: false },
};

/** Waiting this long reads as a caller who needs attention. */
export const LONG_WAIT_MS = 30_000;
/** On hold this long reads as a caller who needs attention. */
export const LONG_HOLD_MS = 60_000;

export function personInitials(name: string | null | undefined): string {
  const parts = (name ?? '')
    .replace(/\(.*?\)/g, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** How long the call has been in its current status. */
export function inStatusMs(call: LiveCall, serverNow: number): number {
  return Math.max(0, serverNow - (call.statusChangedAt || call.startedAt));
}

export function needsAttention(call: LiveCall, serverNow: number): boolean {
  const stage = stageOf(call);
  if (stage === 'waiting') return inStatusMs(call, serverNow) >= LONG_WAIT_MS;
  if (call.status === 'on_hold') return inStatusMs(call, serverNow) >= LONG_HOLD_MS;
  return false;
}

/** A clock time in the company's timezone (never the browser's). */
export function formatClockTime(ms: number | null | undefined, timeZone?: string | null): string {
  if (!ms) return '';
  return formatDateTimeInTimeZone(new Date(ms).toISOString(), timeZone, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export interface JourneyStep {
  key: string;
  label: string;
  at: number | null;
  tone: 'default' | 'ai' | 'agent' | 'hold' | 'end';
  current?: boolean;
}

/** What has happened on this call so far, from its own record. */
export function callJourney(call: LiveCall, ended: boolean): JourneyStep[] {
  const steps: JourneyStep[] = [];
  const line = call.companyNumberLabel || formatPhone(call.companyNumber);
  steps.push({
    key: 'start',
    label:
      call.direction === 'inbound'
        ? `Rang in on ${line || 'your line'}`
        : `Dialed out from ${line || 'your line'}`,
    at: call.startedAt,
    tone: 'default',
  });
  if (call.aiHandled) {
    steps.push({ key: 'ai', label: 'AI receptionist answered', at: null, tone: 'ai' });
  }
  (call.agentHistory ?? []).forEach((stint, index) => {
    const name = stint.name || 'A team member';
    steps.push({
      key: `agent-${index}`,
      label: index === 0 ? `${name} picked up` : `Transferred to ${name}`,
      at: stint.since ?? null,
      tone: 'agent',
    });
  });
  if (!ended && call.status === 'transferring') {
    steps.push({
      key: 'transferring',
      label: `Transferring${call.transferTarget?.name ? ` to ${call.transferTarget.name}` : ''}…`,
      at: call.statusChangedAt,
      tone: 'hold',
      current: true,
    });
  }
  if (!ended && call.status === 'on_hold') {
    steps.push({
      key: 'hold',
      label: 'Put on hold',
      at: call.statusChangedAt,
      tone: 'hold',
      current: true,
    });
  }
  if (ended) steps.push({ key: 'end', label: 'Call ended', at: call.endedAt ?? null, tone: 'end' });
  return steps;
}
